import React, { ReactNode } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import theme from './colors';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Global Theme Provider for Dockal Registry
 *
 * Now using Ant Design's official Cyan color palette (#08979C - Cyan-7)
 * Cyan represents: Hope, Strong
 *
 * Follows Ant Design v5 official theming standards:
 * - Seed Tokens and Component Tokens
 * - Responsive breakpoints (xs, sm, md, lg, xl, xxl)
 * - Grid system and useBreakpoint hook
 * - Dark/Light mode with theme.darkAlgorithm and theme.defaultAlgorithm
 *
 * Dark/Light Mode:
 * - Uses theme.darkAlgorithm for dark mode
 * - Uses theme.defaultAlgorithm for light mode
 * - Persists to localStorage
 * - Auto-detects system preference
 * - Toggle via ThemeContext
 *
 * Ant Design v5 Breakpoints:
 * - xs: 0-575px (mobile)
 * - sm: 576-767px (tablet)
 * - md: 768-991px (small desktop)
 * - lg: 992-1199px (desktop)
 * - xl: 1200-1599px (large desktop)
 * - xxl: 1600px+ (extra large)
 *
 * References:
 * - https://ant.design/docs/react/customize-theme
 * - https://ant.design/docs/spec/colors (Cyan palette)
 * - https://ant.design/components/grid (Responsive)
 * - https://ant.design/theme-editor
 *
 * Usage:
 * import ThemeProvider from './theme/ThemeProvider';
 * import { Grid } from 'antd';
 * import { useTheme } from './contexts/ThemeContext';
 * const { useBreakpoint } = Grid;
 *
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 */
const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { isDarkMode } = useTheme();

  return (
    <ConfigProvider
      theme={{
        // ============================================
        // ALGORITHM - Dark/Light Mode Toggle
        // Uses Ant Design v5 built-in algorithms
        // darkAlgorithm: Automatically adjusts all colors for dark mode
        // defaultAlgorithm: Light mode (default)
        // Can also combine: [darkAlgorithm, compactAlgorithm]
        // ============================================
        algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,

        // ============================================
        // SEED TOKENS (Primary theme configuration)
        // These are the base tokens that Ant Design uses to generate all other tokens
        // ============================================
        token: {
          // ========== SEED TOKENS ==========
          // Primary Color (most important seed token)
          colorPrimary: theme.primary,           // #08979C (Ant Design Cyan-7)

          // Semantic Colors
          colorSuccess: theme.success,           // #52c41a
          colorWarning: theme.warning,           // #faad14
          colorError: theme.error,               // #ff4d4f
          colorInfo: theme.info,                 // #1890ff

          // Typography
          fontFamily: theme.fontFamily,          // Uses global font config
          fontSize: 14,
          fontSizeHeading1: 38,
          fontSizeHeading2: 30,
          fontSizeHeading3: 24,
          fontSizeHeading4: 20,
          fontSizeHeading5: 16,

          // Border Radius
          borderRadius: 12,
          borderRadiusLG: 16,
          borderRadiusSM: 8,
          borderRadiusXS: 6,

          // Control Heights (affects inputs, buttons, selects)
          controlHeight: 40,
          controlHeightLG: 48,
          controlHeightSM: 32,

          // ========== ALIAS TOKENS ==========
          // Let the algorithm handle colors automatically for dark/light mode
          // Only override link colors to maintain brand consistency

          // Link Colors
          colorLink: theme.primary,
          colorLinkHover: theme.primaryHover,
          colorLinkActive: theme.primaryActive,

          // Spacing (used by Ant Design's spacing system)
          marginXS: 8,
          marginSM: 12,
          margin: 16,
          marginMD: 20,
          marginLG: 24,
          marginXL: 32,
          marginXXL: 48,

          paddingXS: 8,
          paddingSM: 12,
          padding: 16,
          paddingMD: 20,
          paddingLG: 24,
          paddingXL: 32,

          // Box Shadows
          boxShadow: `0 2px 8px ${theme.shadow}`,
          boxShadowSecondary: `0 6px 16px ${theme.shadowMedium}`,

          // Line Heights
          lineHeight: 1.5715,
          lineHeightHeading1: 1.2105,
          lineHeightHeading2: 1.2667,
          lineHeightHeading3: 1.3333,
          lineHeightHeading4: 1.4,
          lineHeightHeading5: 1.5,

          // Motion/Animation
          motionDurationFast: '0.1s',
          motionDurationMid: '0.2s',
          motionDurationSlow: '0.3s',
          motionEaseInOut: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
          motionEaseOut: 'cubic-bezier(0.215, 0.61, 0.355, 1)',

          // Wire Frame (for development/debugging)
          wireframe: false,
        },

        // ============================================
        // COMPONENT TOKENS
        // Customize specific components without affecting others
        // ============================================
        components: {
          // Button Customization
          Button: {
            controlHeight: 40,
            controlHeightLG: 48,
            controlHeightSM: 32,
            borderRadius: 12,
            borderRadiusLG: 14,
            borderRadiusSM: 8,
            fontWeight: 500,
            primaryShadow: `0 4px 12px ${theme.focusRingSolid}`,
          },

          // Input Customization
          Input: {
            controlHeight: 40,
            controlHeightLG: 48,
            controlHeightSM: 32,
            borderRadius: 12,
            paddingBlock: 10,
            paddingBlockLG: 14,
            paddingBlockSM: 8,
            activeBorderColor: theme.borderFocus,
            hoverBorderColor: theme.primary,
            activeShadow: `0 0 0 3px ${theme.focusRing}`,
          },

          // Select Customization
          Select: {
            controlHeight: 40,
            controlHeightLG: 48,
            controlHeightSM: 32,
            borderRadius: 12,
          },

          // Card Customization
          Card: {
            borderRadius: 16,
            borderRadiusLG: 20,
            borderRadiusSM: 12,
            boxShadow: `0 2px 8px ${theme.shadowLight}`,
          },

          // Table Customization
          Table: {
            borderRadius: 12,
            padding: 16,
            paddingContentVertical: 12,
          },

          // Modal Customization
          Modal: {
            borderRadius: 16,
            boxShadow: `0 12px 48px ${theme.shadowDark}`,
          },

          // Alert Customization
          Alert: {
            borderRadius: 12,
          },

          // Progress Customization
          Progress: {
            defaultColor: theme.primary,
            remainingColor: theme.borderLight,
          },

          // Tag Customization
          Tag: {
            borderRadius: 8,
          },

          // Message Customization
          Message: {
            borderRadius: 12,
          },

          // Notification Customization
          Notification: {
            borderRadius: 12,
          },

          // Menu Customization
          Menu: {
            itemBorderRadius: 8,
          },

          // Steps Customization
          Steps: {
            iconSize: 32,
          },

          // Form Customization
          Form: {
            labelFontSize: 14,
            itemMarginBottom: 20,
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

export default ThemeProvider;
